import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Scale, Sparkles, Mail, ArrowUp, ChevronRight, Shield } from 'lucide-react';
import { useSiteBrand } from '../components/BrandLogo';

const LAST_UPDATE = '27 de febrero de 2026';

const SECTIONS = [
  { id: 'resumen', num: '00', title: 'Resumen' },
  { id: 's1', num: '01', title: 'Términos de la tienda en línea' },
  { id: 's2', num: '02', title: 'Condiciones generales' },
  { id: 's3', num: '03', title: 'Información precisa, completa y oportuna' },
  { id: 's4', num: '04', title: 'Modificaciones a los servicios y precios' },
  { id: 's5', num: '05', title: 'Productos y servicios' },
  { id: 's6', num: '06', title: 'Precisión en la información de cuenta' },
  { id: 's7', num: '07', title: 'Herramientas opcionales' },
  { id: 's8', num: '08', title: 'Comentarios y reseñas de usuarios' },
  { id: 's9', num: '09', title: 'Información personal' },
  { id: 's10', num: '10', title: 'Errores u omisiones' },
  { id: 's11', num: '11', title: 'Terminación del servicio' },
  { id: 's12', num: '12', title: 'Cambios a los términos' },
  { id: 'contacto', num: '★', title: 'Contacto' },
];

export default function TerminosPage() {
  const brand = useSiteBrand();
  const siteName = brand?.site_name || 'NubesMTY';
  const supportEmail = brand?.contact_email || brand?.support_email || 'contacto@nubesmty.com';
  const [active, setActive] = useState('resumen');
  const [progress, setProgress] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      // Reading progress
      const el = containerRef.current;
      if (el) {
        const total = el.scrollHeight - window.innerHeight;
        const scrolled = Math.max(0, Math.min(window.scrollY - el.offsetTop, total));
        setProgress(total > 0 ? (scrolled / total) * 100 : 0);
      }
      // Active section
      let current = SECTIONS[0].id;
      for (const s of SECTIONS) {
        const node = document.getElementById(s.id);
        if (node && node.getBoundingClientRect().top - 140 <= 0) {
          current = s.id;
        }
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 110;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div ref={containerRef} className="min-h-[calc(100vh-160px)] pb-24">
      {/* Reading progress bar */}
      <div className="fixed top-0 left-0 right-0 h-[3px] z-[60] bg-white/[0.03]">
        <div
          className="h-full bg-gradient-to-r from-orange-500 via-orange-400 to-red-500 transition-[width] duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/3 w-[420px] h-[420px] rounded-full bg-orange-500/15 blur-[100px]" />
          <div className="absolute bottom-0 right-1/3 w-[380px] h-[380px] rounded-full bg-red-600/10 blur-[100px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-6 pt-14 md:pt-20 pb-10 md:pb-14 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1 mb-5">
            <Sparkles className="w-3.5 h-3.5 text-orange-400" />
            <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-orange-300/90">Documento legal</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight">
            Términos del <span className="text-orange-400">servicio</span>
          </h1>
          <p className="mt-4 text-white/60 text-base md:text-lg max-w-2xl mx-auto">
            Reglas claras para navegar y comprar en {siteName}. Léelas con calma; están hechas para protegerte.
          </p>
          <div className="mt-6 flex items-center justify-center gap-3 text-xs text-white/40">
            <Scale className="w-3.5 h-3.5" />
            <span>Última actualización: <span className="text-white/70 font-semibold">{LAST_UPDATE}</span></span>
          </div>
        </div>
      </section>

      {/* Two-column layout */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14 grid md:grid-cols-[240px_1fr] gap-10">
        {/* Sticky index */}
        <aside className="hidden md:block">
          <div className="sticky top-24">
            <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-white/40 mb-4">Índice</p>
            <nav className="space-y-1" data-testid="terminos-index">
              {SECTIONS.map((s) => {
                const isActive = active === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className={`w-full text-left flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? 'bg-orange-500/10 text-orange-300 border-l-2 border-orange-500 pl-[10px]'
                        : 'text-white/55 hover:bg-white/[0.03] hover:text-white'
                    }`}
                    data-testid={`terminos-idx-${s.id}`}
                  >
                    <span className={`font-black text-[10px] tracking-wider ${isActive ? 'text-orange-400' : 'text-white/30'}`}>{s.num}</span>
                    <span className="flex-1 leading-tight">{s.title}</span>
                    {isActive && <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <article className="prose-invert max-w-none space-y-10 text-white/75 leading-relaxed" data-testid="terminos-content">
          {/* Resumen */}
          <Sec id="resumen" num="00" title="Resumen">
            <p>
              Este sitio es operado por <strong className="text-white">{siteName}</strong>. En todo el sitio, cuando se menciona <em>"nosotros"</em>, <em>"nuestro"</em> o <em>"la tienda"</em>, se refiere a {siteName}. Ofrecemos este sitio incluyendo información, herramientas y servicios disponibles para <strong className="text-white">ti</strong>, el usuario, condicionado a la aceptación de los términos, condiciones, políticas y avisos aquí anotados.
            </p>
            <p>
              Al visitar nuestro sitio o realizar una compra, estás aceptando los siguientes términos y condiciones, incluidos aquellos referenciados por hipervínculo. Estos términos aplican a <strong className="text-white">todos los usuarios</strong>: quienes solo navegan, quienes compran, colaboradores o comerciantes asociados de contenido.
            </p>
            <div className="rounded-2xl border border-orange-500/25 bg-orange-500/[0.06] p-5">
              <p className="text-white text-sm">
                <strong>Por favor lee estos términos con cuidado antes de usar el sitio.</strong> Al acceder a cualquier parte del sitio aceptas atenerte a estos términos. Si no aceptas todos los términos, te pedimos no continuar navegando ni utilizar ningún servicio.
              </p>
            </div>
            <p className="text-sm text-white/55">
              Cualquier nueva herramienta agregada a la tienda queda sujeta a estos mismos términos. Podemos actualizar esta página cuando lo consideremos necesario; es tu responsabilidad revisarla periódicamente. Nos reservamos el derecho de cambiar, actualizar o reemplazar cualquier parte de los Términos y Condiciones.
            </p>
          </Sec>

          <Sec id="s1" num="01" title="Términos de la tienda en línea">
            <p>Al aceptar estos términos, <strong className="text-white">reiteras que tienes la mayoría de edad</strong> en tu estado o entidad de residencia, o bien que un adulto autorizado supervisa la operación en tu nombre.</p>
            <ul className="list-disc list-inside space-y-2 text-white/70">
              <li>Está estrictamente prohibido usar nuestros productos para cualquier acto ilegal o no autorizado, incluyendo la violación de leyes de derechos de autor.</li>
              <li>No debes transmitir gusanos, virus o cualquier código de naturaleza destructiva.</li>
              <li>Cualquier violación a estos términos podrá dar como resultado la <strong className="text-white">terminación inmediata</strong> de los servicios.</li>
            </ul>
          </Sec>

          <Sec id="s2" num="02" title="Condiciones generales">
            <p>Nos reservamos el derecho a <strong className="text-white">negar el servicio</strong> a cualquier persona por cualquier motivo y en cualquier momento.</p>
            <p>Entiendes que tu información (excluyendo los datos de tarjetas de crédito o débito):</p>
            <ul className="list-disc list-inside space-y-2 text-white/70">
              <li>Puede transferirse sin encriptar e involucra transmisión de datos por varias redes.</li>
              <li>Puede cambiar para adaptarse a nuevas tecnologías o requerimientos técnicos.</li>
            </ul>
            <p>Los datos de tarjeta se manejan <strong className="text-white">siempre encriptados</strong> en cualquier transferencia (TLS 1.3 y estándar PCI‑DSS). Aceptas no copiar, reproducir, duplicar, revender ni explotar ninguna porción del servicio sin nuestro permiso por escrito.</p>
          </Sec>

          <Sec id="s3" num="03" title="Información precisa, completa u oportuna">
            <p>No nos responsabilizamos si la información disponible en el sitio no es precisa, completa o está actualizada. El material del sitio se provee como <strong className="text-white">información general</strong> y no debe ser tu única fuente de decisión.</p>
            <p>Este sitio puede contener información histórica que, por su naturaleza, no está al día. Nos reservamos el derecho de modificar el contenido cuando lo consideremos necesario, sin obligación de hacerlo. Es tu responsabilidad monitorear los cambios.</p>
          </Sec>

          <Sec id="s4" num="04" title="Modificaciones a los servicios y precios">
            <p>Los precios en el sitio están sujetos a <strong className="text-white">cambios sin previo aviso</strong>. Podemos modificar o descontinuar cualquier producto o servicio en cualquier momento sin previo aviso.</p>
          </Sec>

          <Sec id="s5" num="05" title="Productos y servicios">
            <p>Nuestros productos y servicios están disponibles exclusivamente a través de la tienda en línea. Los inventarios son limitados y sujetos a nuestras políticas de cambios y devoluciones (consulta <Link to="/envios-y-devoluciones" className="text-orange-400 hover:text-orange-300 underline">envíos y devoluciones</Link>).</p>
            <p>Hacemos nuestro mejor esfuerzo para mostrar de manera precisa los colores e imágenes de cada producto. No podemos garantizar que tu pantalla represente fielmente los colores reales.</p>
            <p>Nos reservamos el derecho de <strong className="text-white">limitar la venta</strong> a cualquier persona, zona geográfica o jurisdicción, así como limitar cantidades y disponibilidad. Todas las descripciones y precios pueden cambiar sin aviso.</p>
            <p className="text-sm text-white/55">No garantizamos la calidad de ningún producto, servicio o información, ni que cubran expectativas específicas (ver garantías en la sección de <Link to="/preguntas-frecuentes" className="text-orange-400 hover:text-orange-300 underline">preguntas frecuentes</Link>).</p>
          </Sec>

          <Sec id="s6" num="06" title="Precisión en la información de cuenta">
            <p>Nos reservamos el derecho a cancelar cualquier pedido y podemos, a discreción, limitar o cancelar cantidades por persona, dirección o método de pago. En caso de hacer cambios o cancelaciones, te avisaremos al correo electrónico registrado.</p>
            <p>Aceptas proveer <strong className="text-white">información precisa, completa y actualizada</strong> al crear tu cuenta y al comprar.</p>
          </Sec>

          <Sec id="s7" num="07" title="Herramientas opcionales">
            <p>Al realizar tu compra podemos utilizar herramientas de terceros (pasarelas de pago, mensajería, autenticación) sobre las cuales no tenemos control directo. Aceptas que estas herramientas se proveen "tal cual", sin garantías, representación o condiciones, y no somos responsables por su uso.</p>
          </Sec>

          <Sec id="s8" num="08" title="Comentarios y reseñas de usuarios">
            <p>Nos reservamos el derecho, más no la obligación, de <strong className="text-white">monitorear, editar o eliminar</strong> contenido publicado por usuarios que consideremos ilegal, ofensivo, amenazante, difamatorio, pornográfico, obsceno, objetable o que viole propiedad intelectual de terceros.</p>
          </Sec>

          <Sec id="s9" num="09" title="Información personal">
            <p>Toda la información que compartas con {siteName} se rige por nuestro <strong className="text-white">Aviso de Privacidad</strong>. Puedes revisarlo en cualquier momento y ejercer tus derechos ARCO escribiendo a <a href={`mailto:${supportEmail}`} className="text-orange-400 hover:text-orange-300 underline">{supportEmail}</a>.</p>
          </Sec>

          <Sec id="s10" num="10" title="Errores u omisiones">
            <p>Ocasionalmente pueden aparecer errores u omisiones en descripciones, precios, promociones, cargos de envío o tiempos de disponibilidad. Nos reservamos el derecho a <strong className="text-white">corregir, cambiar o actualizar</strong> dicha información e incluso cancelar pedidos si la información al momento de la compra era incorrecta.</p>
          </Sec>

          <Sec id="s11" num="11" title="Terminación del servicio">
            <p>Estos términos son efectivos hasta su terminación explícita. Puedes terminarlos notificándonos por escrito o simplemente dejando de usar nuestros servicios.</p>
            <p>Si, a nuestro juicio, sospechamos que has violado alguno de estos términos, podremos terminar el acuerdo <strong className="text-white">sin previo aviso</strong>.</p>
          </Sec>

          <Sec id="s12" num="12" title="Cambios a los términos del servicio">
            <p>Nos reservamos el derecho a cambiar los términos del servicio en cualquier momento. Las modificaciones y aclaraciones tienen efecto inmediato al ser publicadas. Si hacemos cambios importantes te notificaremos por este mismo medio con la nueva fecha de "Última actualización".</p>
          </Sec>

          {/* Contact */}
          <div id="contacto" className="scroll-mt-32">
            <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-orange-500/10 to-transparent p-6 md:p-8">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="w-12 h-12 rounded-full bg-orange-500/15 text-orange-400 flex items-center justify-center shrink-0">
                  <Mail className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-[220px]">
                  <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-orange-300">Preguntas o correcciones</span>
                  <h3 className="text-xl md:text-2xl font-black text-white mt-1">¿Necesitas aclarar algo?</h3>
                  <p className="text-white/60 text-sm mt-2">
                    Si deseas acceder, corregir o eliminar tu información, registrar una queja o simplemente quieres más contexto, escríbenos.
                  </p>
                </div>
                <a
                  href={`mailto:${supportEmail}`}
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold text-sm transition-colors"
                  data-testid="terminos-email-btn"
                >
                  <Mail className="w-4 h-4" /> {supportEmail}
                </a>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between text-xs text-white/40">
              <div className="flex items-center gap-2">
                <Shield className="w-3.5 h-3.5" />
                <span>© {new Date().getFullYear()} {siteName}. Todos los derechos reservados.</span>
              </div>
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="inline-flex items-center gap-1.5 text-white/50 hover:text-orange-400 transition"
                data-testid="terminos-back-top"
              >
                <ArrowUp className="w-3.5 h-3.5" /> Volver arriba
              </button>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
}

function Sec({ id, num, title, children }) {
  return (
    <section id={id} className="scroll-mt-32" data-testid={`terminos-sec-${id}`}>
      <div className="flex items-baseline gap-4 mb-4">
        <span className="text-orange-400 font-black text-sm tracking-[0.15em]">{num}</span>
        <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight">{title}</h2>
      </div>
      <div className="space-y-4 text-[15px]">{children}</div>
      <div className="mt-8 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
    </section>
  );
}
